// hooks/useFloatingUIManager.ts
import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  getCreateModalOpen,
  getSelectedTaskId,
  setCreateModalOpen,
  setSelectedTaskId,
} from '../store/tasksSlice';

/**
 * Custom hook to manage floating UI state globally
 * Ensures only one floating UI is open at a time
 */
export const useFloatingUIManager = () => {
  const dispatch = useAppDispatch();
  const selectedTaskId = useAppSelector(getSelectedTaskId);
  const isTaskPanelOpen = useAppSelector(getCreateModalOpen);

  /**
   * Check if any task panel is currently open
   */
  const hasOpenTaskPanel = useRef<boolean>(false);

  /**
   * Check if a specific task should show its panel
   */
  const shouldShowTaskPanel = useCallback(
    (taskId: string) => {
      hasOpenTaskPanel.current = Boolean(taskId === selectedTaskId);
      return selectedTaskId === taskId && Boolean(isTaskPanelOpen);
    },
    [selectedTaskId, isTaskPanelOpen]
  );

  /**
   * Check if hover tooltips should be allowed (only when no panels are open)
   */
  const shouldAllowHover = !hasOpenTaskPanel.current;

  /**
   * Open a task panel (closes any existing panels first)
   */
  const openTaskPanel = useCallback(
    (taskId: string) => {
      dispatch(setSelectedTaskId(taskId));
      dispatch(setCreateModalOpen(taskId));
    },
    [dispatch]
  );

  /**
   * Close the currently open task panel
   */
  const closeTaskPanel = useCallback(() => {
    dispatch(setSelectedTaskId(null));
    dispatch(setCreateModalOpen(false));
  }, [dispatch]);

  /**
   * Toggle a task panel (open if closed, close if open)
   */
  const toggleTaskPanel = useCallback(
    (taskId: string) => {
      if (selectedTaskId === taskId && isTaskPanelOpen) {
        closeTaskPanel();
      } else {
        openTaskPanel(taskId);
      }
    },
    [selectedTaskId, isTaskPanelOpen, openTaskPanel, closeTaskPanel]
  );

  /**
   * Check if a specific task is currently selected
   */
  const isTaskSelected = useCallback(
    (taskId: string) => {
      return selectedTaskId === taskId;
    },
    [selectedTaskId]
  );

  return {
    // State
    selectedTaskId,
    isTaskPanelOpen,
    hasOpenTaskPanel,
    shouldAllowHover,

    // Task panel methods
    shouldShowTaskPanel,
    openTaskPanel,
    closeTaskPanel,
    toggleTaskPanel,
    isTaskSelected,
  };
};
